/* eslint-disable jsx-a11y/alt-text */
import React, { useMemo } from 'react'
import { getById, getImgByTitle, getBySlug } from '@apps/utils/contentful'
import { useSelector } from 'react-redux'
import { Button, Typography } from '@telus/koodo-ui-kit'
import ActivationErrorLayout from '@apps/flows/activation/sections/ActivationErrorLayout'
import Loader, { SIZE } from '@apps/components/Loader'

// Generic error screen for splash, the 404 catch-all and the ErrorBoundary
// fallback. It now renders through the shared "Transaction Summary (Error)"
// layout so every error surface matches the Figma design (there is a single
// error design, not per-route variants). Content still comes from
// `commonElements`; the error code line only shows when an `errorNum` is
// supplied (i.e. the 404 route).
//
// mfeError — optional `{ code, description }` passed from the splash route
// when navigating here after a MFE load failure. When absent, the component
// falls back entirely to Contentful content so other callers (ErrorBoundary,
// 404 route) are unaffected.

// The label that prefixes the code is content-managed, but it is not populated
// in every Contentful environment — where it is missing the MFE code rendered as
// a bare, unlabelled number ("404" floating on its own). Fall back to a built-in
// label so that line always reads as one phrase.
//
// Scoped to the MFE splash route on purpose: the 404 catch-all and the
// ErrorBoundary fallback keep rendering the Contentful label verbatim, so their
// output is unchanged whether or not the copy is present.
const MFE_ERROR_CODE_LABEL = {
  en: 'Error Code:',
  fr: 'Code d’erreur :'
}

const ErrorSection = ({ errorNum = null, mfeError = null }) => {
  const {
    content: { pageContent },
    user: { selectedLanguage }
  } = useSelector((state) => state.common)

  const content = useMemo(() => {
    if (!pageContent) return {}
    const commonElements = getById(pageContent, 'commonElements')?.[selectedLanguage]
    if (!commonElements) return {}
    const { shortTextFields } = commonElements
    return {
      errorImage: getImgByTitle(commonElements, 'errorImage')?.url,
      errorImageAlt: getImgByTitle(commonElements, 'errorImage')?.altText,
      errorHeading: getBySlug(shortTextFields, 'errorHeading')?.value,
      errorBody: getBySlug(shortTextFields, 'errorBody')?.value,
      errorCode: getBySlug(shortTextFields, 'errorCode')?.value,
      errorCTATitle: getBySlug(shortTextFields, 'errorCTATitle')?.value,
      errorCTALink: getBySlug(shortTextFields, 'errorCTALink')?.value
    }
  }, [pageContent, selectedLanguage])

  const {
    errorImage, errorImageAlt, errorHeading, errorBody, errorCode, errorCTATitle, errorCTALink
  } = content

  // `pageContent` is fetched asynchronously, so on a refresh this renders once
  // with no Contentful copy at all — heading, body and image blank, and the CTA
  // label interpolating two undefined values into a literal "undefined
  // undefined" button (MASC-17193). Hold the loader until the copy arrives.
  if (!pageContent) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-screen overflow-hidden bg-white flex items-center justify-center">
        <Loader size={SIZE.MD} loading />
      </div>
    )
  }

  // The label reads as one phrase across both fields ("Go to" + "koodomobile.com")
  // and the link supplies the href, so a CTA missing either would render with a
  // dangling label or no destination. Require both or render nothing.
  const hasCta = Boolean(errorCTATitle && errorCTALink)

  // MFE error overrides the body text and error code; heading stays consistent.
  // All other callers (ErrorBoundary fallback, 404 route) receive no mfeError
  // prop so they continue to render pure Contentful content unchanged.
  const displayBody = mfeError?.description || errorBody
  let displayErrorCode = null
  if (mfeError?.code) {
    const codeLabel = errorCode || MFE_ERROR_CODE_LABEL[selectedLanguage] || MFE_ERROR_CODE_LABEL.en
    displayErrorCode = (
      <Typography tokens={{ fontSize: 16, fontWeight: 400, color: '#000000' }} block>
        {`${codeLabel} ${mfeError.code}`}
      </Typography>
    )
  } else if (errorNum) {
    displayErrorCode = (
      <Typography tokens={{ fontSize: 16, fontWeight: 400, color: '#000000' }} block>
        {errorCode}
        {' '}
        {errorNum}
      </Typography>
    )
  }

  return (
    <ActivationErrorLayout
      imageUrl={errorImage}
      imageAlt={errorImageAlt}
      heading={errorHeading}
      body={displayBody}
      errorCode={displayErrorCode}
    >
      {hasCta ? (
        <div className="w-full sm:w-auto">
          <Button
            href={`https://www.${errorCTALink}`}
            // Without accessibilityRole="link" the kit renders react-native-web's
            // default button role, which resolves to a <button href="..."> — href
            // means nothing on a <button>, and the kit's web branch supplies no
            // press handler to fall back on, so the CTA was inert on every error
            // page. The link role makes it render as a real <a href>.
            accessibilityRole="link"
            variant={{ width: 'full' }}
            tokens={{ backgroundColor: '#000000', color: '#ffffff', borderColor: '#000000' }}
            testId="error-cta"
          >
            {`${errorCTATitle} ${errorCTALink}`}
          </Button>
        </div>
      ) : null}
    </ActivationErrorLayout>
  )
}

export default ErrorSection
